import { useState } from "react";
import { MoreHorizontal, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// App-like bottom navigation for mobile. Desktop uses <Sidebar/> instead.
export function BottomNav({ items, active, onSelect }) {
  const { t } = useI18n();
  const [sheet, setSheet] = useState(false);
  const navItems = items.filter((i) => !i.group);
  const maxDirect = navItems.length > 5 ? 4 : 5;
  const visible = navItems.slice(0, maxDirect);
  const overflow = navItems.slice(maxDirect);
  const activeInOverflow = overflow.some((i) => i.key === active);

  const NavBtn = ({ it }) => {
    const on = active === it.key;
    const Icon = it.icon;
    return (
      <button
        data-testid={`bottomnav-${it.key}`}
        onClick={() => { onSelect(it.key); setSheet(false); }}
        className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-w-0 active:scale-95 transition-transform"
        style={{ color: on ? "var(--accent)" : "var(--text-muted)" }}
      >
        {on && <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full" style={{ background: "var(--accent)" }} />}
        {Icon && <Icon className="h-5 w-5 shrink-0" />}
        <span className="text-[10px] font-medium truncate max-w-full px-0.5">{it.label}</span>
        {it.badge > 0 && (
          <span className="absolute top-1 left-1/2 translate-x-2 text-[9px] font-bold h-4 min-w-[16px] px-1 flex items-center justify-center rounded-full"
            style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>{it.badge}</span>
        )}
      </button>
    );
  };

  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 flex items-stretch border-t"
        style={{ background: "var(--surface)", borderColor: "var(--border)", paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="bottom-nav"
      >
        {visible.map((it) => <NavBtn key={it.key} it={it} />)}
        {overflow.length > 0 && (
          <button
            data-testid="bottomnav-more"
            onClick={() => setSheet(true)}
            className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 active:scale-95 transition-transform"
            style={{ color: activeInOverflow ? "var(--accent)" : "var(--text-muted)" }}
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="text-[10px] font-medium">{t("nav.more")}</span>
          </button>
        )}
      </nav>

      {sheet && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end" data-testid="bottomnav-sheet" onClick={() => setSheet(false)}>
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,.5)" }} />
          <div className="relative rounded-t-2xl p-4 pb-8 kw-fade-up" style={{ background: "var(--surface)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading font-bold" style={{ color: "var(--text)" }}>{t("nav.menu")}</span>
              <button onClick={() => setSheet(false)} data-testid="bottomnav-sheet-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {overflow.map((it) => {
                const on = active === it.key;
                const Icon = it.icon;
                return (
                  <button key={it.key} data-testid={`bottomnav-sheet-${it.key}`} onClick={() => { onSelect(it.key); setSheet(false); }}
                    className="flex flex-col items-center gap-2 p-4 border"
                    style={{ borderRadius: "var(--radius)", borderColor: on ? "var(--accent)" : "var(--border)", background: on ? "var(--surface-alt)" : "transparent", color: on ? "var(--accent)" : "var(--text)" }}>
                    {Icon && <Icon className="h-5 w-5" />}
                    <span className="text-xs font-medium text-center">{it.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
